import * as React from "react";
import { Button, Modal } from "antd";
import { RefreshCw } from "react-feather";
import { NGProviderBrowserProps } from "./types";
import { useProviderBrowserRedux } from "./hooks/useProviderBrowserRedux";
import { useKeyboardNavigation } from "./hooks/useKeyboardNavigation";
import { BrowserColumnHeader } from "./components/BrowserColumnHeader";
import { VirtualizedBody } from "./components/VirtualizedBody";
import { BrowserFooter } from "./components/BrowserFooter";
import { BrowserLoading } from "./components/BrowserLoading";
import { BrowserEmpty } from "./components/BrowserEmpty";
import { measureText } from "../../../services/textMeasure";
import styles from "./NGProviderBrowser.module.scss";

const EMPTY_ARRAY: string[] = [];
const EMPTY_RENDERERS: NGProviderBrowserProps["columnRenderers"] = [];
const EMPTY_TOOLTIPS: NGProviderBrowserProps["tooltips"] = [];

// Constants for virtualization
const ROW_HEIGHT = 40;
const MAX_VISIBLE_ROWS = 12;

export const NGProviderBrowser: React.FC<NGProviderBrowserProps> = ({
  initialValue,
  onOk,
  onClose,
  fetchData,
  labels,
  columnRenderers = EMPTY_RENDERERS,
  getData,
  title,
  rightAlign = EMPTY_ARRAY,
  monoColumns = EMPTY_ARRAY,
  distinctFilters = EMPTY_ARRAY,
  filteredColumns = EMPTY_ARRAY,
  tooltips = EMPTY_TOOLTIPS,
  multipleChoice = false,
  sortable = false,
  initialSort,
  width = 1200,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<any>(null);
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [tableWidth, setTableWidth] = React.useState<number | undefined>(undefined);
  const [showScrollIndicator, setShowScrollIndicator] = React.useState(false);
  const [hasVerticalOverflow, setHasVerticalOverflow] = React.useState(false);

  // Memoize array props to prevent unnecessary re-renders
  const stableLabels = React.useMemo(() => labels, [labels.join(",")]);
  const stableRightAlign = React.useMemo(() => rightAlign, [rightAlign.join(",")]);
  const stableMonoColumns = React.useMemo(() => monoColumns, [monoColumns.join(",")]);
  const stableColumnRenderers = React.useMemo(() => columnRenderers, [columnRenderers.length]);

  const { state, processedItemIds, pinnedCount, itemsMap, selectedIdsSet, actions } = useProviderBrowserRedux({
    initialValue,
    fetchData,
    getData,
    labels,
    distinctFilters,
    filteredColumns,
    initialSort,
  });

  // Store previous column widths to preserve them during re-renders
  const prevColumnWidthsRef = React.useRef<number[]>([]);

  // Calculate column widths using canvas-based text measurement
  const columnWidths = React.useMemo(() => {
    // If no items, preserve previous widths to prevent layout jump
    if (state.items.length === 0) {
      return prevColumnWidthsRef.current;
    }

    const CELL_PADDING = 24; // 12px padding on each side
    const FIRST_COL_EXTRA = 28; // checkbox/radio + gap
    const HEADER_EXTRA = 60; // space for sort icons, distinct filter, etc.
    const FILTER_INPUT_WIDTH = 168; // min-width + margin-left

    const widths: number[] = [];

    for (let colIndex = 0; colIndex < labels.length; colIndex++) {
      const label = labels[colIndex];
      const isMono = monoColumns.includes(label);
      const isFirstCol = colIndex === 0;
      const hasFilterInput = filteredColumns.includes(label);

      // Measure header label width
      let maxWidth = measureText(label, "regular");

      // Find widest text in this column across all items
      state.items.forEach((item) => {
        const data = getData(item);
        const columnData = data[colIndex + 1];
        const displayText = Array.isArray(columnData)
          ? String(columnData[1] || columnData[0])
          : String(columnData || "");
        const textWidth = measureText(displayText, isMono ? "mono" : "regular");
        if (textWidth > maxWidth) {
          maxWidth = textWidth;
        }
      });

      // Apply padding
      let colWidth = maxWidth + CELL_PADDING;
      if (isFirstCol) colWidth += FIRST_COL_EXTRA;

      // Calculate minimum header width based on controls
      let minHeaderWidth = measureText(label, "regular") + CELL_PADDING + HEADER_EXTRA;
      if (hasFilterInput) {
        minHeaderWidth += FILTER_INPUT_WIDTH;
      }
      colWidth = Math.max(colWidth, minHeaderWidth);

      widths.push(Math.ceil(colWidth));
    }

    // Store for future reference
    prevColumnWidthsRef.current = widths;
    return widths;
  }, [state.items, labels, monoColumns, filteredColumns, getData]);

  const handleSelect = React.useCallback(
    (value: string) => {
      if (multipleChoice) {
        actions.toggleSelection(value);
      } else {
        actions.setSelection(value);
      }
    },
    [multipleChoice, actions],
  );

  const handleSelectByIndex = React.useCallback(
    (index: number) => {
      if (index >= 0 && index < processedItemIds.length) {
        const itemValue = processedItemIds[index];
        handleSelect(itemValue);
      }
    },
    [processedItemIds, handleSelect],
  );

  useKeyboardNavigation({
    itemCount: processedItemIds.length,
    focusedIndex: state.focusedIndex,
    onFocusChange: actions.setFocusedIndex,
    onSelect: handleSelectByIndex,
    onClose,
    searchInputRef,
    containerRef,
    isOpen: true,
    listRef,
  });

  const handleCancel = React.useCallback(() => {
    actions.clearSelection();
    onClose();
  }, [actions, onClose]);

  const handleOk = React.useCallback(() => {
    onOk(multipleChoice ? state.selectedValues : state.selectedValues[0]);
    onClose();
  }, [multipleChoice, state.selectedValues, onOk, onClose]);

  const handleColumnFilterChange = React.useCallback(
    (column: string) => (value: string) => {
      actions.setColumnFilter(column, value);
    },
    [actions],
  );

  const handleDistinctFilterToggle = React.useCallback(
    (column: string) => (value: string) => {
      actions.toggleDistinctFilter(column, value);
    },
    [actions],
  );

  const handleSort = React.useCallback(
    (label: string) => () => {
      actions.setSort(label);
    },
    [actions],
  );

  // Calculate container height for virtualization
  const containerHeight = Math.min(processedItemIds.length * ROW_HEIGHT, MAX_VISIBLE_ROWS * ROW_HEIGHT);

  // Calculate total table width from column widths
  React.useEffect(() => {
    if (columnWidths.length > 0) {
      const totalWidth = columnWidths.reduce((sum, w) => sum + w, 0);
      // Add extra padding for scrollbar and any rounding errors
      const EXTRA_PADDING = 20;
      setTableWidth(totalWidth + EXTRA_PADDING);
    }
  }, [columnWidths]);

  // Track horizontal scroll to show/hide gradient indicator
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const checkScroll = () => {
      const hasHorizontalOverflow = container.scrollWidth > container.clientWidth;
      const isAtEnd = container.scrollLeft + container.clientWidth >= container.scrollWidth - 30;
      setShowScrollIndicator(hasHorizontalOverflow && !isAtEnd);
      setHasVerticalOverflow(container.scrollHeight > container.clientHeight);
    };

    // Initial check with slight delay to ensure DOM has updated
    const timeoutId = setTimeout(checkScroll, 0);

    // Use ResizeObserver to detect content size changes
    const resizeObserver = new ResizeObserver(checkScroll);
    resizeObserver.observe(container);

    container.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
      container.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [tableWidth, state.isLoading]);

  // Grid template columns style
  const gridTemplateColumns = columnWidths.length > 0 ? columnWidths.map((w) => `${w}px`).join(" ") : undefined;

  // Calculate modal width: shrink to fit content, but expand up to viewport when needed
  const MODAL_PADDING = 48; // Modal internal padding
  const MAX_VIEWPORT_PERCENT = 0.95;
  const maxViewportWidth = typeof window !== "undefined" ? window.innerWidth * MAX_VIEWPORT_PERCENT : width;
  // Use table width if available (shrink to fit), capped at viewport (expand when needed)
  const MIN_MODAL_WIDTH = 500;
  const modalWidth = tableWidth ? Math.min(Math.max(tableWidth + MODAL_PADDING, MIN_MODAL_WIDTH), maxViewportWidth) : width;

  const modalTitle = (
    <div className={styles.modalTitle}>
      <span>{title}</span>
      <Button
        type="text"
        size="small"
        icon={<RefreshCw size={14} className={state.isLoading ? styles.spinning : ""} />}
        onClick={actions.refetch}
        disabled={state.isLoading}
      />
    </div>
  );

  return (
    <Modal width={modalWidth} open={true} maskClosable={false} title={modalTitle} onCancel={handleCancel} footer={null}>
      <div className={styles.scrollWrapper}>
        <div
          ref={containerRef}
          className={`${styles.container} ${hasVerticalOverflow ? styles.containerWithVerticalScroll : ""}`}
        >
          {state.error ? (
            <BrowserEmpty error={state.error} />
          ) : state.isLoading ? (
            <BrowserLoading />
          ) : (
            <div className={styles.tableWrapper} style={{ "--column-count": labels.length } as React.CSSProperties}>
              {/* Header Row */}
              <div ref={headerRef} className={styles.headerRow} style={{ gridTemplateColumns }}>
                {labels.map((label, labelIndex) => {
                  const isFirst = labelIndex === 0;
                  const isLast = labelIndex === labels.length - 1;
                  const tooltip = tooltips.find((t) => t.label === label)?.content;
                  const canFilter = filteredColumns.includes(label);
                  const hasDistinctFilter = distinctFilters.includes(label);

                  return (
                    <BrowserColumnHeader
                      key={label}
                      label={label}
                      isFirst={isFirst}
                      isLast={isLast}
                      tooltip={tooltip}
                      canFilter={canFilter}
                      filterValue={state.columnFilters[label] || ""}
                      onFilterChange={handleColumnFilterChange(label)}
                      hasDistinctFilter={hasDistinctFilter}
                      distinctValues={state.allDistinctValues[label] || []}
                      filteredDistinctValues={state.filteredDistinctValues[label] || []}
                      onDistinctFilterToggle={handleDistinctFilterToggle(label)}
                      sortable={sortable}
                      sortKey={state.sortKey}
                      sortType={state.sortType}
                      onSort={handleSort(label)}
                    />
                  );
                })}
              </div>

              {/* Body Rows - Virtualized */}
              {processedItemIds.length === 0 ? (
                <BrowserEmpty />
              ) : (
                <VirtualizedBody
                  processedItemIds={processedItemIds}
                  itemsMap={itemsMap}
                  selectedIdsSet={selectedIdsSet}
                  focusedIndex={state.focusedIndex}
                  labels={stableLabels}
                  multipleChoice={multipleChoice}
                  rightAlign={stableRightAlign}
                  monoColumns={stableMonoColumns}
                  columnRenderers={stableColumnRenderers}
                  onSelect={handleSelect}
                  onFocus={actions.setFocusedIndex}
                  listRef={listRef}
                  containerHeight={containerHeight}
                  tableWidth={tableWidth}
                  hasVerticalOverflow={processedItemIds.length > MAX_VISIBLE_ROWS}
                  gridTemplateColumns={gridTemplateColumns}
                  pinnedCount={pinnedCount}
                />
              )}
            </div>
          )}
        </div>
        <div className={`${styles.scrollIndicator} ${!showScrollIndicator ? styles.scrollIndicatorHidden : ""}`} />
      </div>

      <BrowserFooter
        selectedCount={state.selectedValues.length}
        multipleChoice={multipleChoice}
        onCancel={handleCancel}
        onOk={handleOk}
        disabled={state.selectedValues.length === 0}
      />
    </Modal>
  );
};
